import { action, makeObservable, observable } from "mobx";
import { IParticipant, ITournament, ITournamentGame } from "../interfaces/tournament";

class TournamentStore {

  activeTournament: ITournament | null = null;
  tournamentGames: Map<number, ITournamentGame[]> = new Map();
  loadingGames: boolean = false;
  mainModalIsOpened = false;

  // Simplified leaderboard state
  topPlayers: Map<number, IParticipant[]> = new Map();
  currentUserParticipant: Map<number, IParticipant> = new Map();
  loadingLeaderboard: boolean = false;

  constructor() {
    makeObservable(this, {
      activeTournament: observable,
      tournamentGames: observable,
      loadingGames: observable,
      topPlayers: observable,
      currentUserParticipant: observable,
      loadingLeaderboard: observable,
      mainModalIsOpened: observable,
      setActiveTournament: action.bound,
      setTournamentGames: action.bound,
      setLoadingGames: action.bound,
      setTopPlayers: action.bound,
      setCurrentUserParticipant: action.bound,
      setLoadingLeaderboard: action.bound,
      setMainModalIsOpened: action.bound,
    });
  }

  setActiveTournament = (tournament: ITournament | null) => {
    this.activeTournament = tournament;
  }

  setTournamentGames = (tournamentId: number, games: ITournamentGame[]) => {
    this.tournamentGames.set(tournamentId, games);
  }

  setLoadingGames = (loading: boolean) => {
    this.loadingGames = loading;
  }

  getTournamentGames = (tournamentId: number): ITournamentGame[] => {
    return this.tournamentGames.get(tournamentId) || [];
  }

  // Simplified leaderboard methods
  setTopPlayers = (tournamentId: number, players: IParticipant[]) => {
    this.topPlayers.set(tournamentId, players);
  }

  setCurrentUserParticipant = (tournamentId: number, participant: IParticipant) => {
    this.currentUserParticipant.set(tournamentId, participant);
  }

  setLoadingLeaderboard = (loading: boolean) => {
    this.loadingLeaderboard = loading;
  }

  getTopPlayers = (tournamentId: number): IParticipant[] => {
    return this.topPlayers.get(tournamentId) || [];
  }

  getCurrentUserParticipant = (tournamentId: number): IParticipant | null => {
    return this.currentUserParticipant.get(tournamentId) || null;
  }

  setMainModalIsOpened = (data: boolean) => {
    this.mainModalIsOpened = data;
  }

  // Helper method to get participants with ranks (for top players)
  getTopPlayersWithRanks = (tournamentId: number): Array<IParticipant & { rank: number }> => {
    const topPlayers = this.getTopPlayers(tournamentId);
    return topPlayers.map((player, index) => ({
      ...player,
      rank: index + 1
    }));
  }

  // Helper method to calculate prize based on placement
  calculatePrizeForPlacement = (placement: number): number => {
    if (!this.activeTournament?.prizeBreakDowns) {
      return 0;
    }

    const prizeBreakdown = this.activeTournament.prizeBreakDowns.find(
      breakdown => placement >= breakdown.from && placement <= breakdown.to
    );

    return prizeBreakdown ? prizeBreakdown.prize : 0;
  }

  // Helper method to format userId for display
  formatUserId = (userId: string): string => {
    if (userId.length <= 6) {
      return userId; // Return as is if too short
    }
    return `${userId.substring(0, 3)}...${userId.substring(userId.length - 3)}`;
  }

}

export const tournamentStore = new TournamentStore();
